import { cloneElement, isValidElement, useState, type ReactNode } from 'react';
import { Clock, type LucideIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';

import { BudgetExceededCard } from '@/components/features/billing/cards/BudgetExceededCard';
import { lessonApi } from '@/lib/api-client';
import { parseUtcDate } from '@/lib/datetime';
import { describeFailure } from '@/lib/lesson-error';
import { notify } from '@/lib/notify';
import { handleStepConflict } from '@/lib/stepConflict';
import { optimisticallyMarkStepProcessing } from '@/lib/optimisticRetry';
import type { StepView } from '@/types/pipeline';

import { PanelLoading } from './PanelLoading';
import { PanelGenerate } from './PanelGenerate';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';

/**
 * Single-mode step gate.
 *
 * The backend now hands us a fully-resolved `StepView` per step (state +
 * in_flow + on_demand + error), so this component is one linear decision tree
 * - no more pipeline-mode vs artifact-mode reconciliation. The `view` is looked
 * up from the page-level `steps` map (`usePipelineStatus().data.steps`); every
 * registry step is always present once the page is ready, so an undefined view
 * only ever means "not loaded yet."
 *
 * Decision tree:
 *   !lessonReady / no view   → neutral fetch skeleton
 *   failed                   → FailureCard (retry; BudgetExceeded → paywall)
 *   queued | processing      → skeleton (+ "queued" pill while queued);
 *                              children while processing when `streaming`
 *   completed                → children (renderer fetches its artifact now)
 *   not_started:
 *       in_flow              → queued skeleton (orchestrator hasn't reached it)
 *       !lessonDone          → "Waiting" placeholder (off-flow, lesson building)
 *       on_demand + generate → Generate CTA
 *       else                 → children
 */

export type GateStep =
  | 'text'
  | 'mindmap'
  | 'video'
  | 'quiz'
  | 'flashcards'
  | 'glossary'
  | 'highlights';

interface StepGateGenerateConfig {
  icon: LucideIcon;
  /** Lowercase noun for "Generate {label}" / "Waiting - {label}". */
  label: string;
  description: string;
  onGenerate: () => void;
}

export interface StepGateProps {
  step: GateStep;
  lessonId?: string;
  /** Per-step views from the page-level pipeline-status poll. */
  steps?: Record<string, StepView>;
  /** Lesson-level terminal flag - drives the off-flow "waiting vs generate" split. */
  lessonDone?: boolean;
  /** True once metadata + first pipeline-status snapshot have landed. */
  lessonReady?: boolean;
  /** Per-step skeleton element (e.g. `<VideoSkeleton />`). */
  skeleton: ReactNode;
  /** Off-flow / on-demand Generate CTA. Omit for always-in-flow steps. */
  generate?: StepGateGenerateConfig;
  /** True while a generate/retry mutation for this step is in flight. */
  isGenerating?: boolean;
  /**
   * Custom retry handler. If omitted, the gate dispatches the unified
   * `lessonApi.retryStep(lessonId, step)`.
   */
  onRetry?: () => void;
  /**
   * The step shows its content while it is still being written (the lesson
   * text arrives section by section): render the children while processing
   * instead of the skeleton. The children show their own in-progress state.
   */
  streaming?: boolean;
  children: ReactNode;
}

export function StepGate({
  step,
  lessonId,
  steps,
  lessonDone,
  lessonReady,
  skeleton,
  generate,
  isGenerating = false,
  onRetry,
  streaming = false,
  children,
}: StepGateProps) {
  const view = steps?.[step];

  // Page data hasn't landed yet - hold a neutral "Loading X" skeleton rather
  // than guessing a state from defaults.
  if (lessonReady === false || !view) {
    return cloneSkeletonWithMode(skeleton, 'fetching');
  }

  const { state, in_flow: inFlow, on_demand: onDemand, error } = view;

  if (state === 'failed') {
    return (
      <FailureCard
        step={step}
        lessonId={lessonId}
        error={error}
        onRetry={onRetry}
        retryBusy={isGenerating}
        customTitle={
          generate
            ? `${capitalize(generate.label)} generation failed`
            : undefined
        }
      />
    );
  }

  if (state === 'processing' && streaming && !isGenerating) {
    return <>{children}</>;
  }

  if (state === 'queued' || state === 'processing' || isGenerating) {
    return cloneSkeletonWithPending(
      skeleton,
      state === 'queued' || isGenerating
    );
  }

  if (state === 'completed') {
    return <>{children}</>;
  }

  // state === 'not_started'
  if (inFlow) {
    // Terminal lesson but this in-flow step never ran → it never will (e.g.
    // an artifact that failed to dispatch, or a foundation failure that
    // stranded it). Surface a failure with retry instead of a skeleton that
    // would otherwise spin forever.
    if (lessonDone) {
      return (
        <FailureCard
          step={step}
          lessonId={lessonId}
          error={error}
          onRetry={onRetry}
          retryBusy={isGenerating}
        />
      );
    }
    // In the flow but the orchestrator hasn't scheduled it yet - show queued.
    return cloneSkeletonWithPending(skeleton, true);
  }
  if (!lessonDone) {
    // Off-flow while the lesson is still building: the Generate option unlocks
    // once context is ready (lesson terminal). Until then, a waiting hint.
    return (
      <PanelLoading
        icon={Clock}
        title={`Waiting - ${capitalize(generate?.label ?? step)}`}
        description='Available once the current generation finishes. The Generate option will unlock then.'
      />
    );
  }
  if (onDemand && generate) {
    return (
      <PanelGenerate
        icon={generate.icon}
        title={`Generate ${generate.label}`}
        description={generate.description}
        busy={isGenerating}
        onClick={generate.onGenerate}
      />
    );
  }
  return <>{children}</>;
}

// ─── Helpers ────────────────────────────────────────────────────────

function cloneSkeletonWithPending(
  skeleton: ReactNode,
  pending: boolean
): ReactNode {
  if (!pending) return skeleton;
  if (!isValidElement(skeleton)) return skeleton;
  return cloneElement(skeleton as React.ReactElement<{ pending?: boolean }>, {
    pending: true,
  });
}

/** Inject `mode='fetching'` so the skeleton reads "Loading X" / "Fetching your
 *  x." instead of the generation-flavored "Preparing X". */
function cloneSkeletonWithMode(
  skeleton: ReactNode,
  mode: 'generating' | 'fetching'
): ReactNode {
  if (!isValidElement(skeleton)) return skeleton;
  return cloneElement(
    skeleton as React.ReactElement<{ mode?: 'generating' | 'fetching' }>,
    { mode }
  );
}

function capitalize(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

/**
 * Per-step failure card. Renders `<PanelFallback variant='step'>`. Defaults to
 * the unified `lessonApi.retryStep` when no custom `onRetry` is provided.
 */
function FailureCard({
  lessonId,
  step,
  error,
  customTitle,
  onRetry,
  retryBusy,
}: {
  lessonId?: string;
  step: GateStep;
  error?: StepView['error'];
  customTitle?: string;
  onRetry?: () => void;
  retryBusy?: boolean;
}) {
  const failure = describeFailure(error, step);
  const [isRetrying, setIsRetrying] = useState(false);
  const queryClient = useQueryClient();

  // Budget-exceeded gets a dedicated paywall - a retry would fail identically.
  // The step name IS its backend feature key (`text`, `podcast`, ...), which
  // the card looks up in the usage readback to name the window that ran out.
  if (error?.code === 'BUDGET_EXCEEDED') {
    return <BudgetExceededCard feature={step} />;
  }

  const meta = failure.timestamp
    ? `Failed ${formatDistanceToNow(parseUtcDate(failure.timestamp), { addSuffix: true })} · ${step}`
    : `Step: ${step}`;

  const handleRetry = async () => {
    if (onRetry) {
      onRetry();
      return;
    }
    if (!lessonId) return;
    setIsRetrying(true);
    // Flip the step to "processing" instantly; rollback on a failed queue POST.
    const rollback = optimisticallyMarkStepProcessing(
      queryClient,
      lessonId,
      step
    );
    try {
      await lessonApi.retryStep(lessonId, step);
      notify.success('Retry queued');
    } catch (err: unknown) {
      rollback();
      // 409 = nothing to retry (stale screen / double click): refresh calmly.
      if (!handleStepConflict(err, queryClient, lessonId)) {
        notify.error(err, "Couldn't queue retry");
      }
    } finally {
      setIsRetrying(false);
    }
  };

  const canRetry = !!onRetry || !!lessonId;

  return (
    <PanelFallback
      variant='step'
      title={customTitle ?? failure.title}
      description={failure.body}
      meta={meta}
      primary={
        canRetry
          ? {
              label: 'Retry',
              onClick: handleRetry,
              busy: isRetrying || !!retryBusy,
            }
          : undefined
      }
      secondary={{
        label: 'Reload page',
        onClick: () => window.location.reload(),
      }}
    />
  );
}
